import React, { useEffect } from 'react';
import { DatabaseIcon, LockIcon } from 'lucide-react';
import { StepProps, TITLE_PROFILE_MAP } from '../../types/onboarding';
const TITLES = Object.keys(TITLE_PROFILE_MAP);
const ROLES = ['CEO', 'VP of Sales', 'Division Lead', 'Team Lead', 'Loan Officer', 'Jr. Loan Officer', 'Processing Director', 'Processor Manager', 'Processor', 'Jr Processor', 'Solution Desk', 'IT', 'HR', 'Training'];
const DIVISION_LEADS = ['Marcus Rivera', 'Diana Chen', 'Robert Hayes'];
const TEAM_LEADS = ['Sarah Mitchell', 'James Park', 'Lisa Torres', 'Kevin Wright', 'Amy Foster'];
const BRANCHES = ['Ladera Ranch, CA', 'Irvine, CA', 'San Diego, CA', 'Los Angeles, CA', 'Phoenix, AZ', 'Dallas, TX', 'Houston, TX', 'Denver, CO', 'Seattle, WA', 'Miami, FL'];
const ENHANCED_NOTE_ROLES = ['Standard', 'Manager', 'Admin', 'Read Only'];
export function UserDetails({
  data,
  updateData,
  setCanAdvance
}: StepProps) {
  useEffect(() => {
    const isFormValid = !!(data.firstName.trim() && data.lastName.trim() && data.alias.trim() && data.username.trim() && data.email.trim() && data.title && data.role);
    setCanAdvance(isFormValid);
  }, [data, setCanAdvance]);
  const handleNameChange = (field: 'firstName' | 'lastName', value: string) => {
    const updates: Partial<typeof data> = {
      [field]: value
    };
    const first = field === 'firstName' ? value : data.firstName;
    const last = field === 'lastName' ? value : data.lastName;
    if (first && last && !data.alias) {
      updates.alias = `${first.charAt(0).toLowerCase()}${last.substring(0, 4).toLowerCase()}`;
    }
    updateData(updates);
  };
  const handleTitleChange = (value: string) => {
    updateData({
      title: value,
      profile: TITLE_PROFILE_MAP[value] || ''
    });
  };
  const accountName = data.firstName && data.lastName ? `${data.firstName} ${data.lastName} Database` : 'New User Database';
  const inputClass = 'w-full border-0 border-b border-gray-300 rounded-none px-0 py-2 text-sm focus:outline-none focus:border-navy-600 focus:ring-0 bg-transparent transition-colors';
  const selectClass = `${inputClass} appearance-none cursor-pointer`;
  const labelClass = 'block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1';
  const SelectChevron = () => <div className="pointer-events-none absolute inset-y-0 right-0 top-5 flex items-center px-2 text-gray-400" data-id="element-417" data-mp="1gutzn9073oxm">
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-418" data-mp="1ltog44aox1ek">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" data-id="element-419" data-mp="1pwl30779jjob" />
      </svg>
    </div>;
  const SectionHeader = ({
    title
  }: {
    title: string;
  }) => <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2 mb-5 mt-8" data-id="element-420" data-mp="3roprt9tgu0ut">
      {title}
    </h3>;
  return <div className="space-y-2" data-id="element-421" data-mp="3p2qfnielbi45">
      {/* Identity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-id="element-422" data-mp="3p4jkqt9k1ua1">
        <div data-id="element-423" data-mp="3ka5oypqzdl4u">
          <label className={labelClass} data-id="element-424" data-mp="3kafcm6ojzg7d">
            First Name <span className="text-red-500" data-id="element-425" data-mp="3fbvt0cxfmgi6">*</span>
          </label>
          <input type="text" value={data.firstName} onChange={e => handleNameChange('firstName', e.target.value)} className={inputClass} placeholder="Jane" data-id="element-426" data-mp="3adkis13863zf" />
        </div>
        <div data-id="element-427" data-mp="6xvlvl53m3ct">
          <label className={labelClass} data-id="element-428" data-mp="6xdf3ukt86qn">
            Last Name <span className="text-red-500" data-id="element-429" data-mp="1xhbhwg0yhbm">*</span>
          </label>
          <input type="text" value={data.lastName} onChange={e => handleNameChange('lastName', e.target.value)} className={inputClass} placeholder="Doe" data-id="element-430" data-mp="1zu20xzum00a" />
        </div>
        <div data-id="element-431" data-mp="2ilv5eh8su62g">
          <label className={labelClass} data-id="element-432" data-mp="2ilbnv1n29joy">
            Alias <span className="text-red-500" data-id="element-433" data-mp="2iohb0bc4qbi1">*</span>
          </label>
          <input type="text" value={data.alias} onChange={e => updateData({
          alias: e.target.value
        })} maxLength={8} className={inputClass} placeholder="jdoe" data-id="element-434" data-mp="acwvpe8w9lij" />
        </div>
        <div data-id="element-435" data-mp="5ev8qi8cl21s">
          <label className={labelClass} data-id="element-436" data-mp="5c93ip8xiymq">
            Email <span className="text-red-500" data-id="element-437" data-mp="t3wkaech8tba">*</span>
          </label>
          <input type="email" value={data.email} onChange={e => {
          const email = e.target.value;
          const updates: Partial<typeof data> = {
            email
          };
          if (!data.username || data.username === data.email) {
            updates.username = email;
          }
          updateData(updates);
        }} className={inputClass} placeholder="jane.doe@activelinkmortgage.com" data-id="element-438" data-mp="y39clem4asl9" />
        </div>
        <div className="md:col-span-2" data-id="element-439" data-mp="31arryr18qe3s">
          <label className={labelClass} data-id="element-440" data-mp="31dw4qcsrui62">
            Username <span className="text-red-500" data-id="element-441" data-mp="2wdhseik50m28">*</span>
          </label>
          <input type="email" value={data.username} onChange={e => updateData({
          username: e.target.value
        })} className={inputClass} placeholder="jane.doe@truss.com.prod" data-id="element-442" data-mp="3b9z3d8oezyo9" />
        </div>
      </div>

      {/* Role & Profile */}
      <SectionHeader title="Role & Profile" data-id="element-443" data-mp="2dns1i6jhwkfl" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-id="element-444" data-mp="2sb4sias2nidd">
        <div className="relative" data-id="element-445" data-mp="2sd7orcjti7qj">
          <label className={labelClass} data-id="element-446" data-mp="2nko8u8xnr0ik">
            Title <span className="text-red-500" data-id="element-447" data-mp="2nitmqranh2cw">*</span>
          </label>
          <select value={data.title} onChange={e => handleTitleChange(e.target.value)} className={selectClass} data-id="element-448" data-mp="1zu0pmriakogp">
            <option value="" disabled data-id="element-449" data-mp="2kjuvpfe1rwcs">
              Select Title
            </option>
            {TITLES.map(t => <option key={t} value={t} data-id="element-450" data-mp="2oky9zxgg5kbw">
                {t}
              </option>)}
          </select>
          <SelectChevron data-id="element-451" data-mp="2td7wt4m9m49y" />
        </div>
        <div data-id="element-452" data-mp="2te9libw7epa5">
          <label className={labelClass} data-id="element-453" data-mp="2tej258vczk36">Profile</label>
          <div className="flex items-center border-b border-gray-200 bg-gray-50 rounded-t-md px-3 py-2" data-id="element-454" data-mp="2y9ycvry2kzvd">
            <LockIcon className="w-3.5 h-3.5 text-gray-400 mr-2 flex-shrink-0" data-id="element-455" data-mp="21mlb3uwzkcty" />
            <span className={`text-sm ${data.profile ? 'text-gray-700 font-medium' : 'text-gray-400 italic'}`} data-id="element-456" data-mp="25nndgkm79505">
              {data.profile || 'Auto-assigned from title'}
            </span>
          </div>
        </div>
        <div className="relative" data-id="element-457" data-mp="2fip4jm01ch5d">
          <label className={labelClass} data-id="element-458" data-mp="3m1dk9z4pe7dh">
            Role <span className="text-red-500" data-id="element-459" data-mp="3m0lyfg0wro0e">*</span>
          </label>
          <select value={data.role} onChange={e => updateData({
          role: e.target.value
        })} className={selectClass} data-id="element-460" data-mp="3qyvurtydcunl">
            <option value="" disabled data-id="element-461" data-mp="1ae0jflxfu9qj">
              Select Role
            </option>
            {ROLES.map(r => <option key={r} value={r} data-id="element-462" data-mp="15kp9vd18reux">
                {r}
              </option>)}
          </select>
          <SelectChevron data-id="element-463" data-mp="wm3utruzahyw" />
        </div>
      </div>

      {/* Organization */}
      <SectionHeader title="Organization" data-id="element-464" data-mp="wk0uof0438gg" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-id="element-465" data-mp="rmhfncvlarng">
        <div className="relative" data-id="element-466" data-mp="mnxw1j4gxry9">
          <label className={labelClass} data-id="element-467" data-mp="mn4o4etrwwej">Division Lead</label>
          <select value={data.divisionLead} onChange={e => updateData({
          divisionLead: e.target.value
        })} className={selectClass} data-id="element-468" data-mp="houp1t4xhbnn">
            <option value="" data-id="element-469" data-mp="2c3xanfquw9la">Select Division Lead</option>
            {DIVISION_LEADS.map(d => <option key={d} value={d} data-id="element-470" data-mp="1kyjhinj2wog0">
                {d}
              </option>)}
          </select>
          <SelectChevron data-id="element-471" data-mp="1pva0pjq6k05n" />
        </div>
        <div className="relative" data-id="element-472" data-mp="1c56h70ioqkjw">
          <label className={labelClass} data-id="element-473" data-mp="1c5zqo9913o5q">Team Lead</label>
          <select value={data.teamLead} onChange={e => updateData({
          teamLead: e.target.value
        })} className={selectClass} data-id="element-474" data-mp="1c3dj49m518ni">
            <option value="" data-id="element-475" data-mp="176mw12bx1csl">Select Team Lead</option>
            {TEAM_LEADS.map(t => <option key={t} value={t} data-id="element-476" data-mp="t6u6vbg1f4mq">
                {t}
              </option>)}
          </select>
          <SelectChevron data-id="element-477" data-mp="x75saqg858z9" />
        </div>
        <div className="relative" data-id="element-478" data-mp="x87arfzkkxr0">
          <label className={labelClass} data-id="element-479" data-mp="2nkms227n4iak">Branch</label>
          <select value={data.branch} onChange={e => updateData({
          branch: e.target.value
        })} className={selectClass} data-id="element-480" data-mp="2nl4w3l0jo0t1">
            <option value="" data-id="element-481" data-mp="fw8smzmwdzab">Select Branch</option>
            {BRANCHES.map(b => <option key={b} value={b} data-id="element-482" data-mp="bux3swbuli36">
                {b}
              </option>)}
          </select>
          <SelectChevron data-id="element-483" data-mp="kxog0ycjshvv" />
        </div>
      </div>

      {/* Contact */}
      <SectionHeader title="Contact" data-id="element-484" data-mp="kwd9wrvmqy1l" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-id="element-485" data-mp="zmcb73ihepla">
        <div data-id="element-486" data-mp="zof7g5a89eyg">
          <label className={labelClass} data-id="element-487" data-mp="unhohqhe8gvw">Phone</label>
          <input type="tel" value={data.phone} onChange={e => updateData({
          phone: e.target.value
        })} className={inputClass} placeholder="(555) 123-4567" data-id="element-488" data-mp="umzlmb0874f0" />
        </div>
        <div data-id="element-489" data-mp="3af5mlp858fga">
          <label className={labelClass} data-id="element-490" data-mp="3aevv1x7ga08h">Mobile</label>
          <input type="tel" value={data.mobile} onChange={e => updateData({
          mobile: e.target.value
        })} className={inputClass} placeholder="(555) 987-6543" data-id="element-491" data-mp="35gc8pvoy2mfl" />
        </div>
      </div>

      {/* Additional Settings */}
      <SectionHeader title="Additional Settings" data-id="element-492" data-mp="3pubmt40zcyn3" />
      <div className="space-y-6" data-id="element-493" data-mp="3kvqnr7xgsl08">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6" data-id="element-494" data-mp="3kwjnhoy0aa8v">
          {/* NMLS Toggle */}
          <div data-id="element-495" data-mp="lf24lw2ykwtr">
            <label className={labelClass} data-id="element-496" data-mp="lhoc5vpuncbz">Does this user have an NMLS?</label>
            <div className="flex items-center mt-1.5" data-id="element-497" data-mp="hf1rqfvictot">
              <button type="button" onClick={() => updateData({
              hasNMLS: !data.hasNMLS,
              nmlsNumber: data.hasNMLS ? '' : data.nmlsNumber
            })} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-out focus:outline-none ${data.hasNMLS ? 'bg-navy-600' : 'bg-gray-200'}`} data-id="element-498" data-mp="cn04jywsxik0">
                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${data.hasNMLS ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-499" data-mp="1coevdpvbbfs7" />
              </button>
              <span className="ml-3 text-sm text-gray-600" data-id="element-500" data-mp="1qnek0xitruod">
                {data.hasNMLS ? 'Yes' : 'No'}
              </span>
            </div>
            {data.hasNMLS && <input type="text" value={data.nmlsNumber} onChange={e => updateData({
            nmlsNumber: e.target.value
          })} className={`${inputClass} mt-3`} placeholder="NMLS Number" data-id="element-501" data-mp="1vjv51iuq9sx8" />}
          </div>

          {/* LendingPad User Id */}
          <div data-id="element-502" data-mp="ea6jn0ggl9ca">
            <label className={labelClass} data-id="element-503" data-mp="e9y2uxh7tjvt">LendingPad User Id</label>
            <input type="text" value={data.lendingPadUserId} onChange={e => updateData({
            lendingPadUserId: e.target.value
          })} className={inputClass} placeholder="LP-12345" data-id="element-504" data-mp="e83hji217pr7" />
          </div>

          {/* Enhanced Note Role */}
          <div className="relative" data-id="element-505" data-mp="xwm3zrvnkp27">
            <label className={labelClass} data-id="element-506" data-mp="szm6i5cvcump">Enhanced Note Role</label>
            <select value={data.enhancedNoteRole} onChange={e => updateData({
            enhancedNoteRole: e.target.value
          })} className={selectClass} data-id="element-507" data-mp="t1gj8wdzlgfp">
              <option value="" data-id="element-508" data-mp="38rjln26orq0n">Select Role</option>
              {ENHANCED_NOTE_ROLES.map(r => <option key={r} value={r} data-id="element-509" data-mp="33sp21y0w9xfh">
                  {r}
                </option>)}
            </select>
            <SelectChevron data-id="element-510" data-mp="1ja4pe7v5bi3c" />
          </div>
        </div>
      </div>

      {/* Account Preview */}
      <div className="mt-8 bg-gray-50 rounded-lg p-4 flex items-center space-x-4 border border-gray-100" data-id="element-511" data-mp="1o7o396nxlsur">
        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-gray-100" data-id="element-512" data-mp="1aeyh928eknxn">
          <DatabaseIcon className="w-5 h-5 text-navy-600" data-id="element-513" data-mp="10ibxkgrtc2x7" />
        </div>
        <div data-id="element-514" data-mp="10l7tjzk3ohj0">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5" data-id="element-515" data-mp="15homd728vk2f">
            Account Record to be created
          </p>
          <p className="text-sm font-semibold text-gray-900" data-id="element-516" data-mp="rjrkpw8xongw">{accountName}</p>
        </div>
      </div>
    </div>;
}